import { FolderTreeSelect } from '../../CommonComponents/FolderTreeSelect/FolderTreeSelect';
import { FOLDER_SELECT_MENU_HEIGHT } from '@opengov/stories-shared/constants';
import { IPublication, IStory } from '@opengov/stories-shared/types';
import { isEmpty } from 'lodash';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useMemo, useState } from 'react';

import { ISessionUser, ISettings, IStoriesFolder, IStoryListMessages } from '../../../../types';

import { useCreateStoryFromTemplate } from '../hooks';
import { styles } from './CreateStoryFromTemplateModal.styles';

export interface ICreateStoryFromTemplateModalProps {
  canOrganizeStories: boolean;
  currentFolderId?: string;
  entityId: number;
  entityName: string;
  entitySubdomain: string;
  folderStructure: IStoriesFolder[];
  isAdmin: boolean;
  messages: IStoryListMessages;
  settings: ISettings;
  selectedTemplate: IStory;
  user: ISessionUser;
  publication?: IPublication;
  onClose(): void;
}

export const CreateStoryFromTemplateModal: React.FC<ICreateStoryFromTemplateModalProps> = (props) => {
  const {
    isAdmin,
    canOrganizeStories,
    currentFolderId,
    folderStructure,
    messages,
    entityId,
    entitySubdomain,
    settings,
    selectedTemplate,
    user,
    publication,
    onClose
  } = props;
  const createStoryFromTemplateMutation = useCreateStoryFromTemplate();
  const [storyTitle, setStoryTitle] = useState(selectedTemplate.title || '');
  const trimmedStoryTitle = useMemo(() => storyTitle.trim(), [storyTitle]);
  const [storyDescription, setStoryDescription] = useState(selectedTemplate.description || '');
  const [folderId, setFolderId] = useState(
    currentFolderId ? currentFolderId : folderStructure && folderStructure[0] && folderStructure[0].id
  );

  const handleStoryCreate = () => {
    createStoryFromTemplateMutation.mutate({
      isAdmin,
      user,
      entityId,
      entitySubdomain,
      templateId: selectedTemplate._id,
      settings,
      title: trimmedStoryTitle,
      description: storyDescription,
      folderId,
      paddingTop: selectedTemplate.paddingTop,
      paddingBottom: selectedTemplate.paddingBottom,
      customCssCode: selectedTemplate.customCssCode,
      publication
    });
    onClose();
  };

  return (
    <div data-test="create-story-from-template-modal">
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="create-story-from-template-dialog-title">
        <DialogTitle id="create-story-from-template-dialog-title" sx={styles.dialogTitle}>
          {messages.createStoryFromTemplate}
          <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <Box data-test="create-story-form">
            <Box sx={styles.formItem}>
              <Box sx={styles.labelRow}>
                <Typography id="create-from-template-title-label" variant="body2" sx={styles.formLabel}>
                  {messages.title}
                </Typography>
                <Typography variant="caption" sx={styles.characterCounter}>
                  {trimmedStoryTitle.length}/60
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                sx={styles.textField}
                placeholder={messages.storyName}
                value={storyTitle}
                onChange={(e) => setStoryTitle(e.target.value)}
                inputProps={{ 'data-test': 'story-title-input', maxLength: 60, className: 'no-format', 'aria-labelledby': 'create-from-template-title-label' }}
              />
            </Box>

            <Box sx={styles.formItem}>
              <Box sx={styles.labelRow}>
                <Typography id="create-from-template-description-label" variant="body2" sx={styles.formLabel}>
                  {messages.description}
                </Typography>
                <Typography variant="caption" sx={styles.characterCounter}>
                  {storyDescription.length}/150
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                sx={styles.textField}
                placeholder={messages.storyDescription}
                value={storyDescription}
                onChange={(e) => setStoryDescription(e.target.value)}
                inputProps={{ 'data-test': 'story-description-input', maxLength: 150, className: 'no-format', 'aria-labelledby': 'create-from-template-description-label' }}
              />
            </Box>

            {canOrganizeStories && (
              <Box sx={styles.listItem}>
                <Typography variant="body2" sx={styles.formLabel}>
                  {messages.location}
                </Typography>
                <FolderTreeSelect
                  activeNodeId={folderId}
                  data={folderStructure}
                  dataTest="folder-accordion-select"
                  disabled={!folderStructure}
                  maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
                  onNodeSelect={(node) => setFolderId(node.id)}
                  showFilter={true}
                  filterPlaceholder={messages.filterFolders}
                />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button variant="outlined" color="secondary" size="small" onClick={onClose} data-test="cancel-create-story-button">
            {messages.cancel}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            disabled={
              trimmedStoryTitle.length < 1 ||
              trimmedStoryTitle.length > 60 ||
              storyDescription.length > 150 ||
              isEmpty(folderId)
            }
            onClick={handleStoryCreate}
            data-test="create-story-button"
          >
            {messages.create}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
